import React, { useState, useEffect } from 'react';
import { UserCheck, X } from 'lucide-react';
import { Modal, ModalBody, ModalFooter, Button } from './ui';
import { KPIService } from '../services/kpiService';
import './ModelBuilderAssignmentModal.css';

/**
 * Modal for assigning a model builder to a project.
 * Shown when moving from Modeling → Double Check if no builder is assigned.
 */
const ModelBuilderAssignmentModal = ({
  isOpen,
  onClose,
  onSubmit,
  projectName,
  jobNumber,
}) => {
  const [selectedBuilderId, setSelectedBuilderId] = useState('');
  const [builders, setBuilders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Fetch model builders on mount
  useEffect(() => {
    const fetchBuilders = async () => {
      try {
        setLoading(true);
        const data = await KPIService.getModelBuilders();
        setBuilders(data);
      } catch (err) {
        console.error('Error fetching model builders:', err);
        setError('Failed to load model builders');
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      fetchBuilders();
    }
  }, [isOpen]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedBuilderId('');
      setError(null);
      setSubmitting(false);
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    if (!selectedBuilderId) {
      setError('Please select a model builder');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await onSubmit(parseInt(selectedBuilderId, 10));
    } catch (err) {
      console.error('Error assigning model builder:', err);
      setError('Failed to assign model builder');
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal trackAs="ModelBuilderAssignmentModal" isOpen={isOpen} onClose={onClose} size="md" zIndex={10000}>
      {/* Header */}
      <div className="model-builder-assignment-header">
        <div className="model-builder-assignment-header-content">
          <div className="model-builder-assignment-icon">
            <UserCheck size={28} color="white" />
          </div>

          <div>
            <h1 className="model-builder-assignment-title">
              Assign Model Builder
            </h1>
            <p className="model-builder-assignment-subtitle">
              {jobNumber} - {projectName}
            </p>
          </div>
        </div>

        <button onClick={onClose} className="model-builder-assignment-close">
          <X size={20} />
        </button>
      </div>

      <ModalBody>
        <div className="model-builder-assignment-body">
          <p className="model-builder-assignment-description">
            A model builder must be assigned before moving to Double Check.
            This assignment is used for KPI tracking.
          </p>

          {loading ? (
            <div className="model-builder-assignment-loading">
              Loading model builders...
            </div>
          ) : (
            <>
              <label className="model-builder-assignment-label">
                Select Model Builder
              </label>
              <select
                value={selectedBuilderId}
                onChange={(e) => {
                  setSelectedBuilderId(e.target.value);
                  setError(null);
                }}
                className="model-builder-assignment-select"
              >
                <option value="">-- Select a model builder --</option>
                {builders.map((builder) => (
                  <option key={builder.id} value={builder.id}>
                    {builder.name}
                  </option>
                ))}
              </select>

              {error && (
                <div className="model-builder-assignment-error">
                  {error}
                </div>
              )}
            </>
          )}
        </div>
      </ModalBody>

      <ModalFooter>
        <Button variant="ghost" onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={!selectedBuilderId || loading || submitting}
        >
          {submitting ? 'Assigning...' : 'Assign & Continue'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ModelBuilderAssignmentModal;
